import React from "react";
import { useTranslate } from "./useTranslate";
import { useNavigate } from "react-router-dom";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { useAppSelector } from "../../store/hooks";
import { ActionDataT } from "../../models/models";
import usePopup from "./usePopup";
import { toast } from "react-toastify";
import { TOAST_DURATION } from "../helpers/constants";
import Button, { ButtonModeE } from "../../components/Button/Button";
import { H1, P } from "../../components/Text/Text";
import { useWizardCreator } from "../../popups/Wizard/useWizardCreator";
import { WizardModeE, WizardTypeE } from "../../popups/Wizard/models";
import { AccessLevelE, ProjectStatusE, UserRoleE } from "../../models/enums";
import { RoutesE } from "../../routes/routesData";
import { JUMBO_DELIVERY_NAME } from "../constants";
import { useUserDeleteCampaignMutation } from "../../api/campaignAPI";
import ChangeInternalProjectNumber from "../../popups/ChangeInternalProjectNumber";

import styles from "../styles/popup.module.scss";

const useCampaignActions = (
  campaignNumber: number,
  createdByUser: string,
  campaignDeliveriesAmount: number,
  firstDeliveryId: number,
  lastDeliveryId: number,
  deliveryStatuses?: number[]
): {
  defaultActions: ActionDataT[];
  hasAvailableActions: boolean;
  handleCreateDelivery: () => void;
  handleChangeInternalNumber: (internalNumber: string) => void;
} => {
  const user = useAppSelector(selectCurrentUser);
  const navigate = useNavigate();
  const { createPopup, removePopup } = usePopup();
  const createWizard = useWizardCreator();
  const { t } = useTranslate();

  const [deleteCampaignMutation] = useUserDeleteCampaignMutation();

  const isActionShown =
    user.role === UserRoleE.GlobalAdmin ||
    user.role === UserRoleE.GlobalAdminAndAdminContentManager ||
    user.role === UserRoleE.AdvertisingAgency || // all shown campaigns in the list are shown only if they was created by logged client so there is no need to filter it
    createdByUser?.includes(JUMBO_DELIVERY_NAME) ||
    user.userName === createdByUser;

  const canBroadcasterDeleteInnerCampaign =
    user.role === UserRoleE.Broadcaster &&
    deliveryStatuses?.includes(ProjectStatusE.InnerCampaign);

  // A media-company user (Admin/User, not Guest) may edit their own campaigns. The list is already
  // scoped to their media company by the backend, so no per-row ownership check is needed here.
  const isMediaCompanyNonGuest =
    user.role === UserRoleE.MediaCompany &&
    user.accessLevel !== AccessLevelE.Guest;

  const handleEdit = () => {
    const wizardPopup = createWizard({
      mode: WizardModeE.Edit,
      type: WizardTypeE.Campaign,
      campaignDeliveryId: firstDeliveryId,
    });
    createPopup(wizardPopup);
  };

  const handleCopy = () => {
    const wizardPopup = createWizard({
      mode: WizardModeE.Clone,
      type: WizardTypeE.Campaign,
      campaignDeliveryId: firstDeliveryId,
    });
    createPopup(wizardPopup);
  };

  const handleDelete = () => {
    const deleteCampaign = async () => {
      const res: any = await deleteCampaignMutation({ id: campaignNumber });
      if (!res?.error) {
        toast.success(t`Successfully deleted`, {
          autoClose: TOAST_DURATION,
        });
        navigate(RoutesE.CAMPAIGNS);
        removePopup();
      } else {
        toast.error(t`Something went wrong`, {
          autoClose: TOAST_DURATION,
        });
        removePopup();
      }
    };

    createPopup({
      header: <H1>Delete campaign</H1>,
      body: <P>{t`Do you wish to delete the campaign ${campaignNumber}?`}</P>,
      footer: (
        <div className={styles.cancelProjectFooterButtonsWrapper}>
          <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
            Cancel
          </Button>
          <Button onClick={deleteCampaign} useGlobalLoading>
            Delete
          </Button>
        </div>
      ),
      className: styles.cancelProjectPopupWrapper,
    });
  };

  const handleCreateDelivery = () => {
    const popupBody = createWizard({
      type: WizardTypeE.Delivery,
      mode: WizardModeE.Create,
      campaignDeliveryId: firstDeliveryId,
      deliveryId: lastDeliveryId,
    });
    createPopup(popupBody);
  };

  const handleChangeInternalNumber = async (internalNumber: string) => {
    createPopup(
      ChangeInternalProjectNumber(firstDeliveryId, removePopup, internalNumber)
    );
  };

  const actions: ActionDataT[] = [
    {
      name: "Edit campaign",
      icon: "edit",
      action: handleEdit,
      accessFor: [
        UserRoleE.AdvertisingAgency,
        UserRoleE.GlobalAdmin,
        UserRoleE.GlobalAdminAndAdminContentManager,
        UserRoleE.Broadcaster,
        UserRoleE.DeliveryService,
        UserRoleE.MediaCompany,
      ],
      condition: isActionShown || isMediaCompanyNonGuest,
    },
    {
      name: "Clone campaign",
      icon: "copy",
      action: handleCopy,
      accessFor: [UserRoleE.AdvertisingAgency],
      condition: isActionShown,
    },
    {
      name: "Delete campaign",
      icon: "bin",
      action: handleDelete,
      accessFor: [UserRoleE.AdvertisingAgency, UserRoleE.Broadcaster],
      // A broadcaster may delete a campaign ONLY when it is an internal campaign
      // (has an InnerCampaign delivery). The agency "no deliveries" path below is
      // reachable for broadcasters too (isActionShown is true for campaigns created
      // by JumboDelivery / themselves), so it must be excluded for them. The backend
      // (CampaignsController.UserDelete) enforces the same rule.
      condition:
        (user.role !== UserRoleE.Broadcaster &&
          isActionShown &&
          !campaignDeliveriesAmount) ||
        canBroadcasterDeleteInnerCampaign,
    },
  ];

  const hasAvailableActions = !!actions.filter(
    ({ action, condition, accessFor }) =>
      action !== null &&
      condition &&
      (Array.isArray(accessFor)
        ? accessFor.some((role) => role === user.role)
        : accessFor === user.role)
  )?.length;

  return {
    defaultActions: actions,
    hasAvailableActions,
    handleCreateDelivery,
    handleChangeInternalNumber,
  };
};

export default useCampaignActions;
